/**
 * Regra única de "quais papéis este usuário tem" no frontend (at-81, ADR 0044).
 *
 * Espelha `src/common/papeis.util.ts` do backend, e a simetria é deliberada: a tela decide o que
 * OFERECE, o backend decide o que PERMITE. Divergir faria a interface prometer o que a API nega
 * (ou esconder o que ela liberaria).
 *
 * ⚠️ Funções puras, sem hook: são usadas dentro e fora de componente (o `App.tsx` decide rota, o
 * `Sidebar` monta menu, o resolver de home roda em navegação). Os hooks ficam em `hooks/usePapeis`.
 */

export interface PortadorDePapeis {
  userType?: string | null;
  papeis?: string[] | null;
}

/**
 * 🔴 O FALLBACK é obrigatório: token emitido antes da at-81 não tem o claim `papeis` e vale por
 * até 2h. Sem cair para o papel principal, todo mundo logado perderia acesso na virada do deploy.
 * Claim vazio também cai no fallback — `[]` nunca significa "sem acesso a nada".
 */
export function papeisDe(portador: PortadorDePapeis | null | undefined): string[] {
  if (!portador) return [];

  const lista = Array.isArray(portador.papeis) ? portador.papeis.filter(Boolean) : [];
  if (lista.length > 0) {
    return lista.map((papel) => String(papel).toUpperCase());
  }

  return portador.userType ? [String(portador.userType).toUpperCase()] : [];
}

export function temPapel(
  portador: PortadorDePapeis | null | undefined,
  ...papeis: string[]
): boolean {
  const doUsuario = papeisDe(portador);
  return papeis.some((papel) => doUsuario.includes(String(papel).toUpperCase()));
}

/** ADMIN tem bypass em toda a autorização — em qualquer posição da lista. */
export function ehAdmin(portador: PortadorDePapeis | null | undefined): boolean {
  return temPapel(portador, 'ADMIN');
}

/** Interseção entre os papéis do usuário e uma lista permitida (rota, item de menu, ação). */
export function alcanca(
  portador: PortadorDePapeis | null | undefined,
  permitidos: string[] | null | undefined,
): boolean {
  if (ehAdmin(portador)) return true;
  if (!Array.isArray(permitidos) || permitidos.length === 0) return false;
  return temPapel(portador, ...permitidos);
}

/**
 * Os quatro papéis do Contac são visões mutuamente excludentes do mesmo dado financeiro — a
 * combinação é recusada pelo backend, e a tela desabilita as opções para o erro não chegar como
 * 400 depois de a pessoa preencher o formulário inteiro.
 */
export const PAPEIS_FINANCEIRO_EXCLUSIVOS = [
  'CONTAC',
  'CONSULTOR',
  'REGIONAL',
  'RELACIONAMENTO',
] as const;

export function ehPapelFinanceiroExclusivo(papel: string): boolean {
  return (PAPEIS_FINANCEIRO_EXCLUSIVOS as readonly string[]).includes(String(papel).toUpperCase());
}

/** Qual dos papéis exclusivos já está no conjunto — para desabilitar os outros três na tela. */
export function exclusivoSelecionado(papeis: string[]): string | null {
  const encontrado = papeis.find((papel) => ehPapelFinanceiroExclusivo(papel));
  return encontrado ? String(encontrado).toUpperCase() : null;
}
